if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dots: this.querySelector('.carousel-dots'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }

        if (this.elements.dots) {
          this.elements.dots.querySelectorAll('[data-target]').forEach((dot) => {
            dot.addEventListener('click', this.setActiveMedia.bind(this, dot.dataset.target, false));
          });
        }

        // Carousel layout: these dedicated .carousel-arrow buttons exist only
        // for this layout and only at >=750px (hidden via CSS below that).
        // They are entirely separate from .slider-button, which we never
        // touch, so there's no shared listener or reference to collide with
        // whatever slider-component does for mobile's native-scroll arrows.
        if (this.dataset.desktopLayout === 'carousel') {
          const desktopPrev = this.querySelector('.carousel-arrow--prev');
          const desktopNext = this.querySelector('.carousel-arrow--next');
          if (desktopPrev) desktopPrev.addEventListener('click', this.onCarouselArrowClick.bind(this, -1));
          if (desktopNext) desktopNext.addEventListener('click', this.onCarouselArrowClick.bind(this, 1));
        }

        if (!this.elements.thumbnails && !this.elements.dots) return;

        if (this.dataset.desktopLayout.includes('thumbnail') && this.elements.thumbnails && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      onCarouselArrowClick(direction) {
        const mediaIds = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]')).map(
          (el) => el.dataset.mediaId
        );
        if (mediaIds.length < 2) return;

        const activeEl =
          this.elements.viewer.querySelector('.product__media-item[data-media-id].is-active') ||
          this.elements.viewer.querySelector('.product__media-item[data-media-id]');
        const currentIndex = mediaIds.indexOf(activeEl.dataset.mediaId);
        let nextIndex = currentIndex + direction;
        if (nextIndex < 0) nextIndex = mediaIds.length - 1;
        if (nextIndex >= mediaIds.length) nextIndex = 0;

        this.setActiveMedia(mediaIds[nextIndex], false);
      }

      onSlideChanged(event) {
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }
        if (this.elements.dots) {
          const dot = this.elements.dots.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );
          this.setActiveDot(dot);
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`.product__media-item[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('.product__media-item[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }

        if (this.elements.dots) {
          const activeDot = this.elements.dots.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveDot(activeDot);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      setActiveDot(dot) {
        if (!this.elements.dots || !dot) return;

        this.elements.dots.querySelectorAll('.carousel-dot').forEach((element) => {
          element.classList.remove('is-active');
          element.removeAttribute('aria-current');
        });
        dot.classList.add('is-active');
        dot.setAttribute('aria-current', 'true');
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}